// The play record the client database holds and /api/sync exchanges, and the rule for choosing between two
// versions of one play. The server applies the same rule to pushes as clients apply to pulls, so every copy
// converges on the same version whatever order syncs happen in. See docs/client-synced-database.md.

import type { MoveAction, PlayData } from '../types';

export interface SyncedPlay<CellT = unknown> {
  /** Client-generated UUID, or `s:<plays.id>` for a row created before sync existed. */
  cid: string;
  boardId: number;
  /** The board's game and date, filled in by the server from the board; null until it has. A slug board
   * (a tutorial, a practice puzzle) has no date. */
  gameId: number | null;
  boardDate: string | null;
  /** The slug of a tutorial or practice board, from the board like the two above; null for a day's puzzle.
   * Absent on a copy stored before plays carried it, which a full pull fills in. */
  boardSlug?: string | null;
  /** For a weekly challenge board, the Monday of its week, from the board like the others; null otherwise.
   * Absent on a copy stored before plays carried it, which can only be a play of some other board. */
  boardWeek?: string | null;
  /** Epoch ms when the play was started. Set once, by whichever client started it. */
  createdAt: number;
  complete: boolean;
  /** Epoch ms of the completion; null while incomplete. */
  completedAt: number | null;
  /** Seconds of play. Only grows as a play continues, which is why it orders incomplete versions. */
  duration: number;
  /** `playData.savedAt` is when this version was last changed. Cells are null once complete. */
  playData: PlayData<CellT>;
  /** The move list behind undo. A completed play carries it only on its way to the server, which files it
   * away and hands the play back without it. */
  moves: MoveAction[] | null;
  /** Epoch ms when the player reset this completion's day to unplayed in the archive calendar; null or absent
   * if they haven't. Only the server sets it. */
  resetAt?: number | null;
  /** The server's change sequence for this version; null until the server has acknowledged it. */
  seq: number | null;
}

/** Brings a version to its canonical shape: a completed play keeps no cells, an unfinished one has moves. */
export function normalizePlay<CellT>(play: SyncedPlay<CellT>): SyncedPlay<CellT> {
  if (!play.complete) return play.moves ? play : { ...play, moves: [] };
  if (play.playData.cells === null) return play;
  return { ...play, playData: { ...play.playData, cells: null as unknown as CellT[][] } };
}

/*
  plays.play_duration is a float4, so a duration comes back from the server rounded to single precision.
  Compared at full precision, a client's 12.3456789 would outrank the server's copy of the very same
  version, stay dirty, and be pushed on every sync forever.
*/
const durationKey = (play: SyncedPlay) => Math.fround(play.duration);

/** JSON with object keys sorted: jsonb hands objects back in its own key order. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

const sign = (n: number) => (n > 0 ? 1 : n < 0 ? -1 : 0);

/**
 * Orders two versions of the same play: positive when `a` should win, negative when `b` should, zero when
 * they're the same version. A completion beats any progress and is then frozen, the earliest one winning;
 * then the one reset most recently; among incomplete versions the one with the most play in it wins, then the most recently changed. The last
 * resort compares content, so the order is total and two copies never settle on different winners.
 *
 * The clock comes before the move count because undo and reset take moves away without adding time (the
 * timer counts whole seconds), and the version after them has to win over the one before.
 */
export function comparePlayVersions(a: SyncedPlay, b: SyncedPlay): number {
  if (a.complete !== b.complete) return a.complete ? 1 : -1;
  if (a.complete) {
    const byCompletion = (b.completedAt ?? Infinity) - (a.completedAt ?? Infinity);
    if (byCompletion) return sign(byCompletion);
    const byDuration = durationKey(b) - durationKey(a);
    if (byDuration) return sign(byDuration);
    // A reset is the server's to make, so a copy from before it never outranks one carrying it.
    const byReset = (a.resetAt ?? 0) - (b.resetAt ?? 0);
    if (byReset) return sign(byReset);
  } else {
    const byDuration = durationKey(a) - durationKey(b);
    if (byDuration) return sign(byDuration);
    const bySaved = (a.playData.savedAt ?? 0) - (b.playData.savedAt ?? 0);
    if (bySaved) return sign(bySaved);
    const byMoves = (a.moves?.length ?? 0) - (b.moves?.length ?? 0);
    if (byMoves) return sign(byMoves);
  }
  // A completion is the same version with or without its moves: the server's copy never has them, and a
  // device's must not outrank it for still holding them.
  const ka = canonicalJson([a.playData, a.complete ? null : a.moves]);
  const kb = canonicalJson([b.playData, b.complete ? null : b.moves]);
  return ka < kb ? 1 : ka > kb ? -1 : 0;
}

/** The winning version of two copies of one play; `held` is kept when they're the same version. */
export function mergePlay<CellT>(held: SyncedPlay<CellT>, incoming: SyncedPlay<CellT>): SyncedPlay<CellT> {
  return comparePlayVersions(incoming, held) > 0 ? incoming : held;
}
